import { useMemo } from 'react';
import { dayjs } from '@koob/dates';
import { useAsync } from 'react-async';
import { useTranslation } from 'react-i18next';
import { KoobSkeleton } from '@koob/ui';

import { getHotelActiveContractsDetailed } from 'api/node/contracts';
import KoobTitle from '../../../components/Koob/KoobTitle';
import KoobParagraph from '../../../components/Koob/KoobParagraph';
import NightRatesEditor from '../../Hotels/Hotel/Contracts/EditContract/NightRatesEditor';

// Hotel-level wrapper around the reusable NightRatesEditor: it resolves the data
// the editor needs (currency / permission from the hotel, the period grid from
// the hotel's active published contracts) and hands it a single merged contract.
// `hotel` comes from the parent already formatted; `currency` / `canUpdateHotel`
// are passed through from the parent's getHotel call to avoid a redundant fetch.
export default function NightRatesHotelEditor({
  hotel,
  currency: currencyProp,
  canUpdateHotel: canUpdateHotelProp,
}) {
  const { t } = useTranslation('contracts');

  const {
    data: contractsData,
    isLoading: isContractsLoading,
    error: contractsError,
  } = useAsync({
    promiseFn: getHotelActiveContractsDetailed,
    hotelId: hotel.id,
    watchFn: (prev, next) => prev.hotelId !== next.hotelId,
  });

  const contracts = contractsData?.data ?? [];

  // Merge the rooms of every active published contract into a single room list,
  // grouping periods by hotel room. The editor keys the grid off periods, so one
  // room row can span periods coming from different contracts — which is what
  // lets month navigation cross contract boundaries.
  // When multiple contracts cover the same date (overlap during season rollover),
  // the period from the most recently started contract takes precedence.
  const mergedContract = useMemo(() => {
    const roomsByHotelRoom = new Map();

    contracts.forEach((contract) => {
      (contract?.hotelRoomContracts ?? []).forEach((hrc) => {
        const key = hrc?.hotelRoom?.id ?? hrc?.id;
        if (!key) return;
        if (!roomsByHotelRoom.has(key)) {
          roomsByHotelRoom.set(key, {
            id: hrc.id,
            hotelRoom: hrc.hotelRoom,
            periods: [],
          });
        }
        // Periods are tagged with their own contract's group-price flag and
        // minimum stay: one room row can mix periods from several contracts, so
        // both are per-period questions, not per-room ones.
        roomsByHotelRoom.get(key).periods.push(
          ...(hrc.periods ?? []).map((period) => ({
            ...period,
            enableGroupPrices: Boolean(contract?.enableGroupPrices),
            minimumStay: period.minimumStay ?? contract?.minimumStay,
          })),
        );
      });
    });

    // Sort periods newest-first so dayMeta.find (which is now a reduce preferring
    // latest startAt) picks deterministic values across fetch orders.
    for (const room of roomsByHotelRoom.values()) {
      room.periods.sort((a, b) => dayjs(b.startAt).diff(dayjs(a.startAt)));
    }

    return { hotelRoomContracts: Array.from(roomsByHotelRoom.values()) };
  }, [contracts]);

  if (isContractsLoading) {
    return <KoobSkeleton shape="details" />;
  }

  if (contractsError) {
    return (
      <div>
        <KoobTitle>{t('nightRatesEditor.title')}</KoobTitle>
        <KoobParagraph>{t('misc:saveError')}</KoobParagraph>
      </div>
    );
  }

  // No active published contract → no period grid to configure.
  if (mergedContract.hotelRoomContracts.length === 0) {
    return (
      <div>
        <KoobTitle>{t('nightRatesEditor.title')}</KoobTitle>
        <KoobParagraph>{t('nightRatesEditor.placeholder')}</KoobParagraph>
      </div>
    );
  }

  return (
    <NightRatesEditor
      hotelId={hotel.id}
      contract={mergedContract}
      currency={currencyProp}
      canUpdateHotel={canUpdateHotelProp}
    />
  );
}
